import { useState } from "react";
import { routes } from "@/router";
import { CompanyEntryDto } from "@advanced-sales-technologies/backend-client/models/CompanyEntryDto";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  HiOutlineArrowSmallRight,
  HiOutlineCheckCircle,
  HiOutlineExclamationCircle,
  HiOutlineMagnifyingGlass,
  HiOutlinePlus,
} from "react-icons/hi2";
import { useNavigate } from "react-router-dom";

import { useAddToLists } from "@/hooks/api/list/useAddToLists";
import { useInfiniteLists } from "@/hooks/api/list/useInfiniteLists";
import useTierLimit from "@/hooks/useTierLimit";
import { useToast } from "@/hooks/useToast";
import Button from "@/components/atoms/Button";
import FeaturedIcon from "@/components/atoms/FeaturedIcon";
import InputError from "@/components/atoms/input/InputError";
import BasicModalContent from "@/components/atoms/modal-panels/BasicModalContent";
import SpacesLeft from "@/components/atoms/SpacesLeft";
import FormCheckbox from "@/components/molecules/form/FormCheckbox";
import InfiniteComboBox from "@/components/organisms/InfiniteComboBox";

type Props = {
  onClose: (value: boolean) => void;
  preventClose?: boolean;
  selectedCompanies: (CompanyEntryDto | string)[];
  openCreateListModal: (listName: string) => void;
  onSuccess: () => void;
};

type FormValues = {
  selectedLists: Record<string, boolean>;
};

const AddToStaticListModal = ({
  onClose,
  preventClose = false,
  selectedCompanies,
  openCreateListModal,
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const [listName, setListName] = useState("");
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { tierLimit } = useTierLimit();

  const methods = useForm<FormValues>();
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = methods;

  const { addItems, isPending: addItemsIsLoading } = useAddToLists();

  const {
    data: listData,
    isLoading: isLoadingLists,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteLists({ query: listName || null, types: ["STATIC"] });

  const onSubmit = (data: FormValues) => {
    const selectedListIds = Object.keys(data.selectedLists).filter((key) => data.selectedLists[key]);
    if (selectedListIds.length === 0) {
      setError("selectedLists", {
        type: "manual",
        message: t("static.modalAdd.selectMinOne"),
      });
      return;
    }

    addItems(
      { listIds: selectedListIds, items: selectedCompanies },
      {
        onSuccess: () => {
          onSuccess();
          onClose(false);
          showToast({
            featuredIcon: { icon: HiOutlineCheckCircle, color: "success" },
            heading: t("static.addedSuccessfully"),
            children: (
              <Button
                label={t("static.viewLists")}
                onClick={() => navigate(routes.lists)}
                color={"brand"}
                trailingIcon={<HiOutlineArrowSmallRight className={"size-5"} />}
                variants={"text"}
                size={"xs"}
              />
            ),
          });
        },
      },
    );
  };

  return (
    <BasicModalContent
      featuredIcon={<FeaturedIcon icon={HiOutlinePlus} size={"lg"} color={"primary"} />}
      heading={t("static.modalAdd.heading", { count: selectedCompanies.length })}
      preventClose={preventClose}
      body={t("static.modalAdd.description")}
      onClose={onClose}
      data-testid={"add-to-static-list-modal"}
    >
      <FormProvider {...methods}>
        <form
          onSubmit={(e) => {
            clearErrors();
            handleSubmit(onSubmit)(e);
          }}
        >
          <InfiniteComboBox
            leftPlaceholder={<HiOutlineMagnifyingGlass className={"size-5"} />}
            options={listData}
            onQueryChange={setListName}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            label={t("static.modalAdd.search")}
            placeholder={t("static.modalAdd.search")}
            isLoading={isLoadingLists}
            optionsClassName={"flex flex-col gap-3 pt-3"}
            loadingNode={Array.from({ length: 7 }).map((_, index) => (
              <IsLoading key={index} />
            ))}
            renderOption={(option) => (
              <div
                key={option.id}
                className={
                  "flex items-center gap-1.5 pr-2 disabled-within:cursor-not-allowed disabled-within:select-none"
                }
              >
                <FormCheckbox
                  name={"selectedLists." + option.id}
                  label={option.name}
                  sizing={"md"}
                  disabled={!(tierLimit - option.item_count >= selectedCompanies.length)}
                />
                <SpacesLeft listCount={option.item_count} itemsCount={selectedCompanies.length} />
              </div>
            )}
          />
          {errors.selectedLists && (
            <div className={"flex items-center gap-1"}>
              <HiOutlineExclamationCircle className={"size-4 stroke-2 text-error"} />
              <InputError error={errors.selectedLists.message as unknown as string} />
            </div>
          )}
          <div className="mt-4 grid grid-cols-3 gap-3">
            <Button
              type={"button"}
              label={t("cancel")}
              onClick={() => onClose(false)}
              color="natural"
              variants="outline"
            />
            <Button
              type={"button"}
              label={t("static.modalAdd.create")}
              onClick={() => openCreateListModal(listName)}
              color="brand"
              variants="secondary"
            />
            <Button
              type={"submit"}
              label={t("add")}
              color="brand"
              variants="primary"
              isLoading={addItemsIsLoading || isSubmitting}
            />
          </div>
        </form>
      </FormProvider>
    </BasicModalContent>
  );
};

const IsLoading = () => {
  return (
    <div className={"flex animate-pulse items-center gap-2"}>
      <div className={"size-5 rounded bg-gray-300"} />
      <div className={"h-3 w-24 rounded-full bg-gray-300"} />
    </div>
  );
};

export default AddToStaticListModal;
